import {useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate, useParams} from 'react-router'
import WalkMap, {WALK_MAX_ZOOM} from '@/components/map/WalkMap.jsx'
import {useMapConfig} from '@/hooks/useAppConfig'
import {useTripDetails} from '@/hooks/useTrips'
import {getLatestFix, useMyLocation} from '@/hooks/useMyLocation'
import {usePickupArrivalConfig} from '@/hooks/useAppConfig'
import {useArrivedAtPickupPoint} from '@/hooks/useArrivedAtPickupPoint'
import {resolveWalkPlan} from '@/features/navigation/airportWalk'
import {tripFlightRecord} from '@/features/fromAirportArrival/tripFlightRecord'
import {buildWalkRoute} from '@/features/navigation/buildWalkRoute'
import {nearestRouteIndex, nextInstruction, routeHeadingAtIndex} from '@/features/navigation/turnByTurn'
import {HELD_INSTRUCTION_MS} from '@/features/navigation/walkTiming'
import {distanceMeters} from '@/maps/mapboxDrivingRoute'
import WalkInstructionCard from '@/customComponents/fromAirportArrival/WalkInstructionCard'

/**
 * THE WALK — the arrivals hall to this trip's own pickup booth, live.
 *
 * ── ⚠️ THE DESTINATION IS THE TRIP'S BOOTH, NEVER A HARDCODED ONE ─────────────────
 * The start varies with where the passenger came out, since a domestic door and an
 * international one are different places, and the end is whichever booth this terminal and
 * flight scope resolve to. Both come from the surveyed table copied byte for byte from the
 * mobile apps — the same table the driver's side compares against to decide the passenger
 * has arrived. There is no arrival message in this system; each side works it out alone.
 *
 * ── ⚠️ THE CAMERA IS EYE-LEVEL, NOT OVERHEAD ──────────────────────────────────────
 * A floor plan read from directly above tells you the shape of a building. Someone walking
 * out of customs needs the way ahead, so the camera is pitched and close in, with the route
 * running away from them and the signage along it readable.
 *
 * It is NOT at eye level, and it used to be. See `WALK_PITCH`: standing on the concourse
 * looking down it spends the frame on sky and compresses the walk into a band.
 *
 * ⚠️ AND IT IS FIXED TO THEM. The walker may turn it, tilt it and zoom it; they may not
 * MOVE it. See the camera block below for how, and why each half is needed.
 */

/**
 * HOW FAR THE WALKER MAY TILT, and it is NOT where the view opens — see `WALK_PITCH`.
 *
 * ⚠️ MAPBOX'S OWN CEILING IS 60 AND IT CLAMPS IN SILENCE. A request for 85 against a
 * default map simply arrives as 60, which looks like the request was ignored. Raising it
 * here is what lets the walker tilt all the way to eye level if they want to.
 */
const WALK_MAX_PITCH = 85

/**
 * WHERE THE CAMERA OPENS — raised above the floor, looking down at it.
 *
 * ⚠️ 60, NOT THE 85 CEILING, AND THAT IS THE WHOLE COMPOSITION. At 85 the camera stands
 * on the concourse looking along it: the top third of the frame is sky, the horizon cuts
 * across the middle, and the route ahead is compressed into a few pixels just under it —
 * everything past ten metres is unreadable however far in the map is zoomed.
 *
 * Lifted to 60 the same route lies open across the picture. It is still a perspective view
 * — near is bigger, the walk still runs away from the viewer — but it is a view of the
 * WALK rather than of the sky above it.
 *
 * The walker can tilt back down to eye level themselves; this is only the frame they are
 * handed. See `WALK_MAX_PITCH`.
 */
const WALK_PITCH = 60
/**
 * ⚠️ THE OPENING FRAME IS THE CEILING ITSELF — as close to the walker as Mapbox goes.
 *
 * It is written as `WALK_MAX_ZOOM` rather than as the number 22 on purpose: this and the
 * map's own ceiling are THE SAME LIMIT, and a literal here would be a second copy to keep
 * in step. The view opens at the top of the range and the walker can only come back down
 * it.
 *
 * Every earlier value (19, then 20) left them standing back from a concourse drawn at
 * airport detail, with most of the screen spent on empty floor. Indoor tiles carry gates,
 * carousels and shop names well past the ordinary 18 ceiling, so there is real map to see
 * up here — this is not magnification of a blurred picture.
 *
 * Zooming out is theirs to do, down to the component's own `MIN_ZOOM` of 12 — far enough
 * for the terminal in context, not so far that the floor plan stops rendering.
 */
const WALK_ZOOM = WALK_MAX_ZOOM

/**
 * HOW FAR DOWN THE SCREEN THE WALKER SITS, as a fraction of the stage height.
 *
 * ⚠️ A PITCHED CAMERA DOES NOT CENTRE ITS SUBJECT IN THE PICTURE. At 85° the top of
 * the frame is sky and the horizon lands around a third of the way down, so a walker
 * centred in the DATA appears just under it — with the whole bottom half of the stage
 * spent on empty floor between the camera and them.
 *
 * Pushing them down by a fifth of the stage puts the concourse ahead of them across the
 * middle of the picture, which is the part of the walk they are being shown.
 *
 * ⚠️ A FRACTION, NOT A PIXEL COUNT. This stage is `70dvh` — about 430px on a laptop and
 * 750px on a large monitor — so a fixed offset that composed one of those would be barely
 * visible on the other and would push the walker off the bottom of a short one.
 */
const WALK_SCREEN_DROP = 0.2

/**
 * How far the walker may drift before the line is solved again.
 *
 * ⚠️ NOT PER FIX. Forty metres is far enough that the old line has stopped describing the
 * walk and short enough that a wrong turn is caught within a few steps. Re-routing on
 * every position would be a request a second and a line that twitches under the walker.
 */
const ROUTE_REFETCH_METERS = 40

/**
 * How far the walker must actually travel before their disc is redrawn somewhere else.
 * See the hold that reads it — this is a threshold on GPS NOISE, not on the walk.
 */
const MARKER_MOVE_METERS = 5

/**
 * THE LAST PLACE THE WALKER WAS SEEN — module scope, so it outlives the sheet.
 *
 * ⚠️ WITHOUT IT, KEEPING THE MAP ALIVE BUYS NOTHING. `WalkMap` holds its instance across an
 * unmount (see `cache` there), but this screen would still come back with no position and
 * sit on "Building the 3D map…" until a fresh cold GPS fix landed — so a passenger who
 * closed the sheet to read a message and reopened it ten seconds later, standing in the
 * same spot, waited all over again for something already known.
 *
 * ⚠️ A SEED, NOT A SOURCE OF TRUTH. It is the value the screen OPENS with; the live watch
 * is running from the first render and replaces it as soon as it has anything to say. The
 * only way it can be wrong is if the passenger moved while the sheet was shut, and a fix is
 * seconds behind them at walking pace.
 *
 * ⚠️ AND IT DIES WITH THE TAB, deliberately. Persisting a position across reloads means
 * showing someone where they were yesterday.
 */
let lastWalker = null

/**
 * ⚠️ NOT A ROUTE. It is the welcome screen's modal content — `/{tripId}/walk-to-pickup`
 * was removed once both walks moved into that sheet. `tripId` still comes from the router,
 * because the screen underneath is `/{tripId}/welcome`.
 *
 * ⚠️ THE WRITTEN STEPS ARE NOT REACHED FROM HERE. This screen carried a
 * "View Step-by-Step Directions" pill over the map; it is gone, and the welcome screen's
 * own entry to `walkView === 'steps'` is the way in — see `FromAirportArrived`. The map
 * is a full-bleed camera view now and nothing floats over it but the instruction card.
 */
/**
 * ⚠️ IT TAKES ITS `tripId` AS A PROP NOW (2026-09-23), and the `useParams` fallback is
 * only the old route's reading.
 *
 * This read the router alone, on the reasoning written here before: "the screen underneath
 * is `/{tripId}/welcome`". That route was deleted when the arrival guide became a modal, so
 * the screen underneath is whatever page the passenger happened to be on — the home page,
 * their rides, settings — and none of those carry a trip in the URL. `useParams` therefore
 * returned undefined, which is how "I've arrived" navigated to `/undefined/track-driver`
 * and why the trip fetched here was empty.
 *
 * `FromAirportArrived` knows the trip; it passes it in.
 */
/**
 * @param {Function} [onLeave] the walk is OVER — shut the sheet, and the arrival guide
 *   behind it. Navigating alone left both modals stacked over `/track-driver`: the
 *   passenger pressed "I've arrived", the URL changed underneath, and they were still
 *   looking at a walk they had just finished with a map they could not see.
 */
export default function WalkToPickup({tripId: tripIdProp = null, onLeave = null}) {
    const {tripId: routeTripId} = useParams()
    const tripId = tripIdProp ?? routeTripId
    const navigate = useNavigate()
    /* ⚠️ `useTripDetails` RESOLVES TO `{trip, paymentMethods}`, NOT THE TRIP. Read as the
       trip itself, every field below is undefined and the screen renders its empty
       state on a perfectly good booking — silently, because nothing throws. */
    const {data} = useTripDetails(tripId)
    const trip = data?.trip || null
    const {token} = useMapConfig()
    const me = useMyLocation()

    /* ⚠️ SHARED THROUGHOUT, AND THIS IS NOT OPTIONAL PLUMBING. This stream is the only
       thing the driver's side has to work out that the passenger has reached the booth,
       and that test is a fifty metre radius. A walk with sharing off arrives nowhere. */
    /* ⚠️ NO LOCATION SHARING HERE. The portal shares from ONE place — `AppLayout` — on one rule: the first ongoing trip's flight has landed and the ride has not started. A page-level copy would share under its own rule and contradict it. The position is still READ here, for this screen's own map. */

    /* Domestic or international — which arrivals door the walk starts at, and which booth
       it ends at. Read off `trip.flight`, which the backend fills from its own
       provider polling; nothing here calls the flight provider. See `tripFlightRecord`. */
    const flight = tripFlightRecord(trip)

    const plan = useMemo(
        () => resolveWalkPlan(me.position, flight),
        [me.position, flight],
    )

    /*
     * WHERE THE LINE STARTS.
     *
     * ⚠️ THE WALKER'S OWN POSITION OUTRANKS THE DOOR. The door is where an arrivals hall
     * empties out, which is right at the moment they emerge and wrong two minutes later
     * when they are halfway down the concourse — a route from a door they have already
     * left draws a line running backwards past them.
     */
    const from = useMemo(() => {
        if (Number.isFinite(me.position?.lat)) return [me.position.lng, me.position.lat]
        return plan.start || null
    }, [me.position, plan.start])

    const [route, setRoute] = useState(null)
    const solvedFrom = useRef(null)

    useEffect(() => {
        if (!from || !plan.pickup) return undefined
        /* ⚠️ NO DISTANCE GUARD HERE, DELIBERATELY. The doomed-request check lives inside
           `fetchWalkingRoute`, where it can decline the network call WITHOUT also skipping
           the walkway graph and the occupancy grid — the two local sources that are the
           whole of the real Pearson route. See the note there. */
        const last = solvedFrom.current
        if (last && distanceMeters(last, from) < ROUTE_REFETCH_METERS) return undefined
        solvedFrom.current = from

        let cancelled = false
        buildWalkRoute(from, plan.pickup, token).then((solved) => {
            if (!cancelled && solved?.points?.length) setRoute(solved)
        })
        return () => { cancelled = true }
    }, [from, plan.pickup, token])

    /*
     * THE INSTRUCTION, HELD.
     *
     * ⚠️ THE HEADING COMES FROM THE ROUTE, NOT FROM THE WALKER. Consecutive fixes are
     * noisier than a walking pace, so a heading built from position deltas swings wildly
     * while someone walks a straight corridor. The route does not jitter, so the bearing of
     * the segment they are on is the stable answer and only changes at a real corner.
     */
    const [held, setHeld] = useState(null)
    const heldAt = useRef(0)
    const indexRef = useRef(0)

    useEffect(() => {
        const points = route?.points
        if (!points?.length || !Number.isFinite(me.position?.lat)) return

        const here = [me.position.lng, me.position.lat]
        const index = nearestRouteIndex(here, points, indexRef.current)
        indexRef.current = index

        const result = nextInstruction(here, points, index, routeHeadingAtIndex(points, index))
        if (!result) return

        /* ⚠️ `Arrived` INTERRUPTS. Every other instruction waits its turn so the card does
           not flicker between "Continue Straight" and "Slight Left" on GPS noise — but
           holding a turn for three seconds after someone has reached the booth is telling
           them to walk away from the car. */
        const now = Date.now()
        if (result.instruction === 'Arrived' || !held || now - heldAt.current >= HELD_INSTRUCTION_MS) {
            heldAt.current = now
            setHeld(result)
        }
    }, [route, me.position, held])

    /*
     * ARRIVING WITHIN FIFTY METRES OF THE BOOTH ENDS THE WALK.
     *
     * ⚠️ IT PUSHES, SO BACK RETURNS TO THE WELCOME SCREEN. Every other page in the
     * portal behaves this way and this flow was the exception — it replaced the entry, so
     * a passenger who reached tracking from the arrival screen could not get back to it and
     * the browser's back button silently jumped them somewhere else entirely.
     *
     * ⚠️ AND BACK DOES NOT LOOP. The earlier reasoning for replacing was that back would
     * hand someone already in the car a set of walking directions — which was true while
     * the walk was its own ROUTE. It is a view inside the welcome screen's sheet now, and
     * that is local state: back lands on `/{tripId}/welcome` with no sheet open, so there is
     * nothing to re-trigger and nothing to walk out of a second time.
     */
    const {radiusM: pickupRadiusM} = usePickupArrivalConfig('passenger', 'from_airport')
    const arrived = useArrivedAtPickupPoint(me.position, plan.pickup, pickupRadiusM)
    useEffect(() => {
        if (!arrived) return
        navigate(`/${tripId}/track-driver`)
        /* The walker reached the booth on their own — the same ending as pressing
           "I've arrived" on the written steps, so it closes the same two things. */
        onLeave?.()
    }, [arrived, navigate, tripId, onLeave])

    /* The route as the map wants it. Our routers speak `[lng, lat]`; the map speaks
       objects, and converting here keeps the conversion in one place. */
    const pathPoints = useMemo(
        () => (route?.points || []).map(([lng, lat]) => ({lat, lng})),
        [route],
    )

    /*
     * ⚠️ THE CAMERA IS PINNED TO THE WALKER AND STAYS THERE. It is not a map being
     * browsed — it is a view out of the eyes of someone walking, so the one thing it may
     * never do is end up somewhere they are not.
     *
     * Two things enforce that between them, and BOTH are needed:
     *
     *  • Every fix re-issues the request, so the camera walks with them. (The old code
     *    aimed once and then let go, which is why the view drifted behind the walker the
     *    moment they moved off the door.)
     *  • `WalkMap` switches drag-to-pan OFF, so the only camera the walker can reach is the
     *    one they are standing at — a drag turns the view rather than moving it.
     *
     * ⚠️ AND NOTHING COMPETES WITH IT ANY MORE. On the shared map this had to be defended:
     * the route framing ran on its own schedule and would pull the camera back out within
     * seconds, so a focus request had to HOLD it (`cameraHeldRef` in RouteMap) and the
     * framing had to bail out while it was held. `WalkMap` has no framing and no follow
     * mode, so a request is simply where the camera goes.
     *
     * ⚠️ ZOOM AND PITCH ONLY ON THE FIRST AIM — the position goes every time, the framing
     * goes once. Sending all three would undo the walker's own turn, tilt and zoom two
     * seconds after they made it, on the next GPS reading. Only the centre may follow them.
     *
     * ── ⚠️ IT OPENS ON THE WALKER OR IT DOES NOT OPEN (2026-09-23) ───────────────────
     * There is no stand-in camera, and this was tried the other way round first. Because the
     * first GPS fix is slow — `useMyLocation` asks for `enableHighAccuracy` with
     * `maximumAge: 0`, so it is a cold read of the radio — the map briefly opened on the
     * hardcoded YYZ booth instead, on the reasoning that showing the terminal beats showing
     * nothing.
     *
     * It does not. What the passenger saw was a confident 3D view of a place they were not
     * standing, which then jumped somewhere else five seconds later. A map that is wrong and
     * sure of itself is worse than a map that is honestly still looking: this one exists to
     * answer "where am I and which way do I walk", and until the fix lands there is no
     * answer to give. So the stage says it is finding them, and the map is built — once,
     * already tilted, already over the terminal — at their real position.
     *
     * ⚠️ WHICH ALSO MEANS THE OPENING FRAME COSTS NOTHING. `initialCamera` puts the map's
     * birth at the walker, so the first request below has nothing left to move; it is sent
     * as a cut (`duration: 0`) purely to apply the screen offset.
     */
    const aimed = useRef(false)
    const focusId = useRef(0)
    const stageRef = useRef(null)
    const [focusRequest, setFocusRequest] = useState(null)

    /**
     * WHERE THE PIN IS DRAWN — the fix, held still until the walker has actually gone
     * somewhere.
     *
     * ⚠️ A STANDING PHONE DOES NOT REPORT A STANDING POSITION. `useMyLocation` asks for high
     * accuracy with no cached fixes, so readings arrive every couple of seconds and each one
     * differs from the last by a few metres of noise — indoors, under a terminal roof, by
     * more. It only de-duplicates identical coordinates (six decimal places, about 0.1m),
     * which noise never is. So the disc twitched around a spot the walker was standing
     * still on, and the camera glided after it, which reads as drift rather than as GPS.
     *
     * Five metres is comfortably above that jitter and below a walking pace: someone
     * actually moving crosses it in about four steps, so the pin still follows them.
     *
     * ⚠️ AND THE CAMERA IS AIMED AT THE SAME HELD POINT, not at the raw fix. Aiming at the
     * live one would slide the view out from under a pin that had not moved — the two have
     * to agree or the walker is no longer in the middle of their own map.
     *
     * ⚠️ IT IS ONLY THE PICTURE. The route solver, the turn-by-turn instruction and the
     * fifty-metre arrival test all still read `me.position` raw: they measure distances and
     * answer "have I got there", where the newest reading is the best one and a held point
     * would be quietly stale.
     */
    /* ⚠️ ADJUSTED DURING RENDER, NOT IN AN EFFECT. React's own escape hatch for state
       derived from a changing input: setting it here re-renders before anything is
       committed, so the pin is drawn ONCE, at the held point. From an effect it would be
       painted at the jittering fix first and corrected a frame later — the twitch this
       exists to remove, at half the amplitude. */
    /**
     * ⚠️ SEEDED, SO THE SHEET DOES NOT OPEN BLIND. Two places already hold an answer before
     * this screen's own watch has said anything:
     *
     *   `lastWalker`      where this screen last drew them, if it has been open before.
     *   `getLatestFix()`  what any OTHER watch in the tab has seen — and `AppLayout` keeps
     *                     one open the whole time a driver is on the way, so on a real
     *                     arrival the app usually knows the position before the walk is
     *                     ever opened.
     *
     * Without this the screen waited on a cold GPS read it did not need: ten seconds of
     * "Building the 3D map…" while the same tab was posting the passenger's position to the
     * driver every two seconds.
     */
    const [walker, setWalker] = useState(() => lastWalker || getLatestFix())
    /* Kept for the next open — see `lastWalker`. In an effect rather than beside the
       `setWalker` above, because writing a module variable during a render is a side effect
       and React may render this component without committing it. */
    useEffect(() => { if (walker) lastWalker = walker }, [walker])
    const fix = me.position
    if (Number.isFinite(fix?.lat) && Number.isFinite(fix?.lng)
        && (!walker
            || distanceMeters([walker.lng, walker.lat], [fix.lng, fix.lat]) >= MARKER_MOVE_METERS)) {
        setWalker(fix)
    }

    const {lat: walkLat, lng: walkLng} = walker || {}

    /* ⚠️ THE WALKER AND NOTHING ELSE. No door, no booth, no last-known anything — see above:
       a camera aimed at a place the passenger is not is the failure this screen had. */
    useEffect(() => {
        if (!Number.isFinite(walkLat) || !Number.isFinite(walkLng)) return
        const first = !aimed.current
        aimed.current = true
        focusId.current += 1
        setFocusRequest({
            id: focusId.current,
            lat: walkLat,
            lng: walkLng,
            ...(first ? {zoom: WALK_ZOOM, pitch: WALK_PITCH} : {}),
            /* ⚠️ SENT ON EVERY FIX, not only the first — unlike the zoom and the tilt.
               It is not a camera the walker chose, it is where on the stage they belong, so
               dropping it after the first move would let them creep back up to the middle
               of the frame as they walked. Measured now rather than at mount: the sheet is
               a `dvh` box and a phone's toolbars change that height mid-walk. */
            offset: [0, Math.round((stageRef.current?.clientHeight || 0) * WALK_SCREEN_DROP)],
            /* The opening frame is a CUT — see above. Afterwards, matched to the fix interval
               so the camera glides between readings instead of arriving early and then
               sitting still until the next one. */
            duration: first ? 0 : 1500,
        })
    }, [walkLat, walkLng])

    /* ⚠️ A FIXED STAGE, NOT A MEASURED ONE. This used to compute "the window minus my
       top edge", which is right for a page and wrong inside a sheet: the sheet is already a
       capped box in the middle of the viewport, so that sum produces a stage taller than
       its container and the bottom of the map is clipped away. */
    /**
     * ⚠️ THE STAGE IS ALWAYS MOUNTED, the map is not. The stage's height is what the camera
     * offset is measured from (`stageRef`), and measuring it for the first time in the same
     * commit that mounts the map is what keeps the walker off the middle of the frame.
     *
     * ⚠️ AND THE MAP IS ONLY BUILT ONCE THERE IS A FIX — see the camera note above. Mounting
     * it earlier means building it somewhere, and every "somewhere" available before the
     * first fix is a place the passenger is not standing.
     */
    if (!walker) {
        return (
            <div ref={stageRef} className="relative flex h-[70dvh] items-center justify-center overflow-hidden bg-(--bg-grey)">
                <p className="px-8 text-center text-(--text-secondary)">
                    {me.denied
                        /* ⚠️ NOT AN ERROR, AND NOT A DEAD END. The written directions and the
                           photographs are a complete alternative to this screen — the welcome
                           modal offers them as an equal — so a refused permission costs the
                           live map and nothing else. */
                        ? 'Allow location for this page to see the live walk. The step-by-step directions work without it.'
                        : 'Building the 3D map to guide you…'}
                </p>
            </div>
        )
    }

    return (
        <div ref={stageRef} className="relative h-[70dvh] overflow-hidden">
            {/* ⚠️ `WalkMap`, NOT THE SHARED `RouteMap` (2026-09-23). This used to be
                thirteen props of configuration — a style override, an indoor config, a layer
                slot, a zoom ceiling, and five flags whose job was to switch off framing,
                recentring, the origin pin and the drive-shaped line. What it could not switch
                off was the 3D car: the shared map arms that off the camera's real pitch, this
                view opens at 60°, and so every walk downloaded and compiled `three` to
                extrude a vehicle that is not on this map. See `WalkMap` for the rest.

                What is left below is the walk itself — the line, the two ends, the walker,
                and the camera aimed at them. */}
            <WalkMap
                pathPoints={pathPoints}
                destination={plan.pickup ? {lng: plan.pickup[0], lat: plan.pickup[1]} : null}
                passenger={walker}
                focusRequest={focusRequest}
                /* ⚠️ THE MAP IS BORN ON THE WALKER, not at a default centre it would have to
                   fetch a city of tiles for and then leave. This branch only renders once
                   there IS a walker, so the first tiles requested are the ones that stay on
                   screen. Read once, on the constructor — see `initialCamera`; every move
                   after this one is a `focusRequest`. */
                initialCamera={{lng: walkLng, lat: walkLat, zoom: WALK_ZOOM, pitch: WALK_PITCH}}
                maxPitch={WALK_MAX_PITCH}
                /* ⚠️ ARRIVALS IS NOT THE GROUND FLOOR. Mapbox opens on whichever level the
                   building data marks as default, which at YYZ is G — the street. The booths
                   this walk ends at, and the concourse it crosses to reach them, are on 1, so
                   the map opened showing a floor the passenger is not on and a route drawn
                   through it. */
                defaultFloor="1"
                className="absolute inset-0"
            />

            <WalkInstructionCard
                instruction={held?.instruction}
                metres={held?.distanceMeters ?? 0}
                headline={held?.instruction === 'Arrived' ? plan.arrivalHeadline : plan.endLabel}
            />
        </div>
    )
}

export {WalkToPickup}
